import React from 'react';
import { View, ScrollView, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import MetaHead from '@/components/MetaHead';
import useResponsiveness from '@/hooks/useResponsiveness';
import { isDarkTheme } from '@/constants/Colors';

const LLMSPage = () => {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const isDark = isDarkTheme(theme);

  const content = `# Gab AI

> The only AI that does it all—text, images, videos, music, code, and web search in one chat. Intelligent routing, personalized memory, and complete privacy.

## Overview

Gab AI is an independent AI platform built outside the control of Big Tech. We provide access to multiple top-tier AI models (GPT-5, Claude, Gemini, DeepSeek, and more) through a unified interface while respecting your privacy and freedom.

## Core Features

### Multiple AI Models
- Access to GPT-5, Claude, Gemini, DeepSeek, Qwen, Kimi, and other leading models
- Proprietary "Arya AI" model with Christian values and conservative principles
- Unified API for developers
- All models accessible through a single subscription

### Privacy Protection
- User identities are masked from third-party AI providers
- Generalized batch inference requests instead of personal identity tracking
- No data harvesting or profiling
- Your conversations stay private

### Image & Video Generation
- AI-powered image generation
- Video generation capabilities
- Creative tools for content creation

### Web Search Integration
- Real-time web search across AI models
- Up-to-date information access
- Search results integrated with AI responses

### File Management & AI Chat
- Upload and analyze documents with AI
- Chat with your files
- Document understanding and extraction

### Collections & Organization
- Organize conversations and content
- Create custom collections
- Efficient content management

### Custom AI Agents
- Build personalized AI assistants
- Configure agents for specific tasks
- Save and reuse custom agents

### Platform Customization
- Personalized AI experience
- Custom settings and preferences
- Tailored interface options

## Company Information

**Name:** GAB AI INC
**Location:** Clarks Summit, Pennsylvania, United States
**Founded:** 2016
**Type:** Private Company

**Address:**
Gab AI Inc.
1143 Northern Blvd #158
Clarks Summit, PA 18411
United States of America

**Contact:**
- Support: ai@gab.com
- Legal: legal@gab.ai
- General: support@gab.ai

## Pricing

Gab AI operates on a subscription and credit-based model:
- Monthly and annual subscription plans available
- Credit system for pay-as-you-go usage
- All subscriptions and credits are non-refundable
- Access to all AI models with a single subscription

## Privacy & Values

### Core Privacy Promise
Gab AI protects user privacy by masking identities from third-party AI providers. When using models like GPT or Claude through Gab AI, the provider sees the prompt but not who sent it. This architecture prevents tracking, profiling, and data harvesting.

### Christian & Conservative Values
- Platform emphasizes Christian values through proprietary Arya AI model
- Committed to free speech and First Amendment principles
- Independent from Big Tech control and influence
- No political censorship or bias

### No Marketing or Tracking
- No use of personal information for marketing
- No data sales to advertisers
- No targeted advertising
- No tracking across the web

## Technical Information

### API Access
Developers can access all Gab AI models through a unified API:
- Single endpoint for multiple AI providers
- Consistent request/response format
- Credit-based pricing
- Full documentation available

### Platform
- Web-based application
- Mobile-responsive design
- Real-time AI responses
- Secure authentication

### Supported Models
- OpenAI: GPT-5, o-series reasoning models
- Anthropic: Claude (various versions)
- Google: Gemini
- DeepSeek: DeepSeek models
- Alibaba: Qwen models
- Moonshot: Kimi models
- Gab: Arya, our own in-house model
- Additional models continuously added

## Legal & Policies

### Terms of Service
Users must be 18 years or older. Platform governed by Delaware law. Binding arbitration for disputes. See full Terms of Service at /terms-of-service

### Privacy Policy
Comprehensive privacy protections including identity masking from third-party providers. Data processed in the United States. See full Privacy Policy at /privacy-policy

### Refund Policy
All subscriptions and credit purchases are final and non-refundable due to upfront computational resource allocation. Limited exceptions for billing errors. See full Refund Policy at /refund-policy

### Terms of Sale
Automatic renewal for subscriptions, cancellation available anytime (effective at end of billing period). See full Terms of Sale at /terms-of-sale

## Use Cases

- Content creation and writing
- Research and analysis
- Code generation and debugging
- Image and video generation
- Document analysis and chat
- Custom AI assistant development
- Business automation
- Educational purposes
- Creative projects

## Key Differentiators

1. **Privacy First:** Identity masking from third-party AI providers
2. **No Big Tech Control:** Independent platform with no corporate oversight
3. **All Models, One Platform:** Access every major AI model in one place
4. **Christian Values:** Proprietary AI with Christian and conservative principles
5. **Developer Friendly:** Unified API for all models
6. **Transparent Pricing:** Clear, upfront pricing with no hidden fees
7. **Free Speech:** Commitment to First Amendment principles

## Related Sites

- Main Platform: https://gab.ai
- Social Network: https://gab.com
- News & Blog: https://news.gab.com
- YouTube: https://www.youtube.com/@GabdotAI
- X (Twitter): https://x.com/Gab__AI

## Target Audience

- Individuals seeking privacy-focused AI tools
- Christians and conservatives seeking aligned AI
- Developers needing multi-model API access
- Content creators and marketers
- Businesses requiring AI without Big Tech dependencies
- Researchers and analysts
- Anyone valuing free speech and privacy online

## Last Updated

November 5th, 2025`;

  return (
    <View style={{ flex: 1, backgroundColor: colors.background_color }}>
      <MetaHead 
        title="LLMS.txt"
        description="Machine-readable information about Gab AI for large language models, AI assistants, and web crawlers. Structured data about our platform."
        canonical="/llms"
        keywords="llms.txt, Gab AI, AI information, machine readable, structured data"
      />

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ 
          flexGrow: 1,
          paddingTop: isXS ? 100 : 120,
          paddingBottom: 60,
          paddingHorizontal: isXS ? 20 : 40,
        }}
        showsVerticalScrollIndicator={false}
      >
        <View
          style={{
            maxWidth: 800,
            width: '100%',
            marginHorizontal: 'auto',
          }}
        >
          <View
            style={{
              marginBottom: 32,
              paddingBottom: 24,
              borderBottomWidth: 1,
              borderBottomColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
            }}
          >
            <Text
              weight="bold"
              style={{
                fontSize: isXS ? 28 : 36,
                marginBottom: 12,
              }}
            >
              LLMS.txt
            </Text>
            <Text size="m" color="secondary" style={{ lineHeight: 24 }}>
              Machine-readable information about Gab AI for large language models and AI assistants.
            </Text>
          </View>

          <View
            style={{
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
              borderRadius: 16,
              padding: isXS ? 20 : 32,
              borderWidth: 1,
              borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
            }}
          >
            <Text
              style={{
                fontFamily: Platform.OS === 'web' 
                  ? 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' 
                  : 'monospace',
                fontSize: isXS ? 12 : 14,
                lineHeight: isXS ? 20 : 24,
                color: colors.text_color_primary,
                whiteSpace: 'pre-wrap',
              }}
            >
              {content}
            </Text>
          </View>

          <View style={{ marginTop: 32 }}>
            <Text size="s" color="secondary" style={{ textAlign: 'center' }}>
              This page provides structured information for AI systems and web crawlers.
            </Text>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

export default React.memo(LLMSPage);

